import type { ReactNode } from "react";
import { Flag, Info, ListOrdered, Swords } from "lucide-react";

import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";

import { PRIORITY_CONCURRENCY_COPY } from "../constants/messages";
import {
  PRIORITY_LEVELS,
  PRIORITY_RULE_HINT,
  priorityLevelOf,
} from "../constants/priority-levels";
import { CompetingCampaignsPanel } from "./competing-campaigns-panel";
import {
  CompetingPanelState,
  useCompetingCampaigns,
  type UseCompetingCampaignsParams,
} from "./use-competing-campaigns";

export const PriorityConcurrencyTab = {
  PRIORITY: "priority",
  CONCURRENCY: "concurrency",
  COMPETITORS: "competitors",
} as const;

export type PriorityConcurrencyTab =
  (typeof PriorityConcurrencyTab)[keyof typeof PriorityConcurrencyTab];

const TITLE_ID = "priority-concurrency-title";

interface PriorityConcurrencyCardProps {

  priorityField: ReactNode;

  concurrencyField: ReactNode;
  priorityHasError?: boolean;
  concurrencyHasError?: boolean;
  competing: UseCompetingCampaignsParams;
  defaultTab?: PriorityConcurrencyTab;
}

function TabPill({
  children,
  className = "bg-muted text-muted-foreground",
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <span
      className={`max-w-[8rem] truncate rounded-full px-1.5 py-px text-[11px] font-medium leading-4 ${className}`}
    >
      {children}
    </span>
  );
}

function ErrorDot({ show }: { show?: boolean }): JSX.Element | null {
  if (!show) return null;
  return (
    <>
      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-red-600" />
      <span className="sr-only">{PRIORITY_CONCURRENCY_COPY.tabHasError}</span>
    </>
  );
}

const TRIGGER_CLASS =
  "-mb-px inline-flex h-10 items-center gap-1.5 rounded-none border-b-2 border-transparent px-3 text-sm font-medium text-muted-foreground hover:text-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none [&>svg]:h-4 [&>svg]:w-4";

export function PriorityConcurrencyCard({
  priorityField,
  concurrencyField,
  priorityHasError,
  concurrencyHasError,
  competing,
  defaultTab = PriorityConcurrencyTab.PRIORITY,
}: PriorityConcurrencyCardProps): JSX.Element {
  const view = useCompetingCampaigns(competing);
  const group = (competing.group ?? "").trim();
  const rivals =
    (view.result?.sameCustomer.length ?? 0) + (view.result?.sameGame.length ?? 0);
  const level = PRIORITY_LEVELS[priorityLevelOf(competing.priority)];
  const periodMissing = view.state === CompetingPanelState.MISSING_PERIOD;

  return (
    <Card aria-labelledby={TITLE_ID}>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
        <CardTitle id={TITLE_ID} className="text-base">
          {PRIORITY_CONCURRENCY_COPY.title}
        </CardTitle>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0"
                aria-label={PRIORITY_CONCURRENCY_COPY.ruleHintLabel}
              >
                <Info aria-hidden="true" className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>{PRIORITY_RULE_HINT}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue={defaultTab}>
          <TabsList className="flex h-auto w-full justify-start gap-1 overflow-x-auto rounded-none border-b border-border bg-transparent p-0">
            <TabsTrigger value={PriorityConcurrencyTab.PRIORITY} className={TRIGGER_CLASS}>
              <Flag aria-hidden="true" />
              {PRIORITY_CONCURRENCY_COPY.priorityTab}
              <TabPill className={level.badgeClassName}>{level.label}</TabPill>
              <ErrorDot show={priorityHasError} />
            </TabsTrigger>
            <TabsTrigger value={PriorityConcurrencyTab.CONCURRENCY} className={TRIGGER_CLASS}>
              <Swords aria-hidden="true" />
              {PRIORITY_CONCURRENCY_COPY.concurrencyTab}
              <TabPill>
                {competing.competes
                  ? group || PRIORITY_CONCURRENCY_COPY.concurrencyNoGroup
                  : PRIORITY_CONCURRENCY_COPY.concurrencyParallel}
              </TabPill>
              <ErrorDot show={concurrencyHasError} />
            </TabsTrigger>
            <TabsTrigger value={PriorityConcurrencyTab.COMPETITORS} className={TRIGGER_CLASS}>
              <ListOrdered aria-hidden="true" />
              {PRIORITY_CONCURRENCY_COPY.competitorsTab}
              {periodMissing ? (
                <span className="sr-only">
                  {PRIORITY_CONCURRENCY_COPY.competitorsNoPeriod}
                </span>
              ) : (
                <TabPill>
                  {rivals}
                  <span className="sr-only">
                    {" "}
                    {PRIORITY_CONCURRENCY_COPY.competitorsUnit(rivals)}
                  </span>
                </TabPill>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value={PriorityConcurrencyTab.PRIORITY} className="mt-0 pt-4">
            {priorityField}
          </TabsContent>
          <TabsContent value={PriorityConcurrencyTab.CONCURRENCY} className="mt-0 space-y-4 pt-4">
            {concurrencyField}
          </TabsContent>
          <TabsContent value={PriorityConcurrencyTab.COMPETITORS} className="mt-0 pt-4">
            <CompetingCampaignsPanel view={view} group={group} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
